import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { DEFAULT_CURRENCY } from "@shared/const";

interface ImageSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (productType: "digital" | "print", printFormat?: string) => void;
  imageFilename: string;
  photographerId: number;
  allowedPrintFormatIds?: number[]; // restrict selectable formats when a package defines specific ones
  currency?: string;
}

export function ImageSelectionDialog({
  open,
  onClose,
  onConfirm,
  imageFilename,
  photographerId,
  allowedPrintFormatIds,
  currency,
}: ImageSelectionDialogProps) {
  const { t } = useTranslation();
  const [productType, setProductType] = useState<"digital" | "print">("digital");
  const [printFormat, setPrintFormat] = useState<string>("");
  const resolvedCurrency = currency || DEFAULT_CURRENCY;
  const formatCurrency = useMemo(() => (value: string) => `${parseFloat(value || "0").toFixed(2)} ${resolvedCurrency}`, [resolvedCurrency]);

  const { data: printFormats, isLoading } = trpc.printFormats.listActive.useQuery(
    { photographerId },
    { enabled: open && photographerId > 0 }
  );

  // Set default format when formats load
  // Compute filtered formats if package restricts them
  const filteredFormats = (printFormats || []).filter((f) =>
    allowedPrintFormatIds && allowedPrintFormatIds.length > 0
      ? allowedPrintFormatIds.includes(f.id)
      : true
  );

  useEffect(() => {
    if (filteredFormats.length > 0 && !printFormat) {
      setPrintFormat(filteredFormats[0].name);
    }
  }, [filteredFormats, printFormat]);

  const handleConfirm = () => {
    onConfirm(productType, productType === "print" ? printFormat : undefined);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.gallery.selectProductType}</DialogTitle>
          <DialogDescription>
            Select how you would like to purchase this image
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <p className="text-sm text-muted-foreground">
            <strong>{imageFilename}</strong>
          </p>

          <div className="space-y-4">
            <Label>{t.gallery.productType}</Label>
            <RadioGroup
              value={productType}
              onValueChange={(value) => setProductType(value as "digital" | "print")}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="digital" id="digital" />
                <Label htmlFor="digital" className="cursor-pointer">
                  {t.gallery.digital}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="print" id="print" />
                <Label htmlFor="print" className="cursor-pointer">
                  {t.gallery.print}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {productType === "print" && (
            <div className="space-y-4">
              <Label>{t.gallery.selectFormat}</Label>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">{t.gallery.loadingFormats}</p>
              ) : filteredFormats && filteredFormats.length > 0 ? (
                <Select value={printFormat} onValueChange={setPrintFormat}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredFormats.map((format) => (
                      <SelectItem key={format.id} value={format.name}>
                        {format.name} - {formatCurrency(format.price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-destructive">
                  {t.gallery.noFormatsAvailable}
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={onClose}>
              {t.common.cancel}
            </Button>
            <Button onClick={handleConfirm}>
              {t.gallery.addToCartButton}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
